// =============================================================================
// src/features/crisis/crisis-evo-slider.ts
// Slider interativo do grafico de evolucao (hover + teclado, acessivel).
//
// Proveniencia: porte FIEL de assets/crisis.js 94-190 (initEvoInteraction).
//   Guia + ponto seguem o cursor; o valor e interpolado continuamente (sem
//   saltos). A sobreposicao e feita em px porque o SVG usa
//   preserveAspectRatio=none (um <circle> viraria elipse). pointermove e
//   throttled em requestAnimationFrame — sem timers. Teclado: <-/-> ponto a
//   ponto, Home/End nos extremos, com aria-valuetext + regiao live.
//
// SRP: so a interacao do plot de evolucao. A geometria vem de
//   crisis-chart-geometry.ts; a matematica de amostragem de crisis-evo-sampler.ts.
// =============================================================================

import { on, qs } from '../../app/dom';
import type { EvoAreaGeometry } from './crisis-chart-geometry';
import { getEvoSeries } from './crisis-live-store';
import type { EvoSample } from './crisis-evo-sampler';
import { formatMentions, sampleEvoAt, timeLabel } from './crisis-evo-sampler';

/** Limite Y (px) abaixo do qual o tooltip vira para baixo (assets/crisis.js 142). */
const TIP_FLIP_THRESHOLD_PX = 44;

/** Referencias do plot resolvidas uma vez no init (assets/crisis.js 95-104). */
interface EvoPlotRefs {
  readonly plot: HTMLElement;
  readonly guide: HTMLElement;
  readonly dot: HTMLElement;
  readonly tip: HTMLElement;
  readonly tipVal: HTMLElement;
  readonly tipTime: HTMLElement;
  readonly live: HTMLElement | null;
}

/**
 * Liga o slider interativo do plot de evolucao. No-op se o plot/refs faltam
 * (guard de assets/crisis.js 96), preservando o init preguicoso idempotente.
 */
export function bindCrisisEvoSlider(geometry: EvoAreaGeometry): void {
  const refs = collectEvoPlotRefs();
  if (!refs) return;
  new CrisisEvoSlider(refs, geometry).bind();
}

/** Resolve as sub-partes do plot; retorna null se alguma essencial faltar. */
function collectEvoPlotRefs(): EvoPlotRefs | null {
  const plot = qs<HTMLElement>('#crzEvoPlot');
  if (!plot) return null;
  const guide = qs<HTMLElement>('.crz-evo-guide', plot);
  const dot = qs<HTMLElement>('.crz-evo-dot', plot);
  const tip = qs<HTMLElement>('.crz-evo-tip', plot);
  const tipVal = tip && qs<HTMLElement>('.crz-evo-tip-val', tip);
  const tipTime = tip && qs<HTMLElement>('.crz-evo-tip-time', tip);
  if (!guide || !dot || !tip || !tipVal || !tipTime) return null;
  return { plot, guide, dot, tip, tipVal, tipTime, live: qs<HTMLElement>('#crzEvoLive') };
}

/**
 * Controla o plot interativo: pintura (guia/ponto/tooltip) + entrada por
 * ponteiro e por teclado. Uma instancia por plot; bind() liga os listeners.
 */
class CrisisEvoSlider {
  private pendingX: number | null = null;
  private rafId = 0;
  private kbIndex = lastEvoIndex(); // inicia em "agora" (assets/crisis.js 169)

  constructor(private readonly refs: EvoPlotRefs, private readonly geometry: EvoAreaGeometry) {}

  /** Liga ponteiro e teclado (assets/crisis.js 163-189). */
  bind(): void {
    this.bindPointer();
    this.bindKeyboard();
  }

  /** pointermove/down/enter/leave (assets/crisis.js 163-166). */
  private bindPointer(): void {
    const { plot } = this.refs;
    on(plot, 'pointermove', (e) => this.onPointerMove(e));
    on(plot, 'pointerdown', (e) => this.onPointerMove(e));
    on(plot, 'pointerenter', () => plot.classList.add('is-hover'));
    on(plot, 'pointerleave', () => plot.classList.remove('is-hover'));
  }

  /** Throttle de pointermove em rAF; sem timers (assets/crisis.js 150-162). */
  private onPointerMove(e: PointerEvent): void {
    this.pendingX = e.clientX;
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => this.flushPointer());
  }

  /** Consome o ultimo X pendente e pinta a amostra (assets/crisis.js 154-161). */
  private flushPointer(): void {
    this.rafId = 0;
    const rect = this.refs.plot.getBoundingClientRect();
    if (!rect.width || this.pendingX === null) return;
    const relX = clampRange(this.pendingX - rect.left, 0, rect.width);
    this.paint(sampleEvoAt(relX, rect.width, this.geometry, getEvoSeries()), rect);
  }

  /** Pinta guia, ponto e tooltip em pixels (assets/crisis.js 134-147). */
  private paint(sample: EvoSample, rect: DOMRect): void {
    const xPx = (sample.vbX / this.geometry.w) * rect.width;
    const yPx = (sample.vbY / this.geometry.h) * rect.height;
    this.refs.guide.style.transform = `translateX(${xPx.toFixed(1)}px)`;
    this.refs.dot.style.transform = `translate(${xPx.toFixed(1)}px,${yPx.toFixed(1)}px)`;
    this.positionTooltip(xPx, yPx, rect.width);
    this.refs.tipVal.textContent = `${formatMentions(sample.value)} menções`;
    this.refs.tipTime.textContent = timeLabel(sample.pos);
  }

  /** Posiciona o tooltip; vira para baixo sem espaco acima (assets/crisis.js 140-144). */
  private positionTooltip(xPx: number, yPx: number, width: number): void {
    const { tip } = this.refs;
    tip.style.left = `${clampRange(xPx, 40, width - 40).toFixed(1)}px`;
    tip.style.top = `${yPx.toFixed(1)}px`;
    tip.style.transform = yPx < TIP_FLIP_THRESHOLD_PX
      ? 'translate(-50%, 14px)'
      : 'translate(-50%, calc(-100% - 14px))';
  }

  /** keydown (setas/Home/End) + blur (assets/crisis.js 182-189). */
  private bindKeyboard(): void {
    const { plot } = this.refs;
    on(plot, 'keydown', (e) => this.onKeydown(e));
    on(plot, 'blur', () => plot.classList.remove('is-hover'));
  }

  /** Move o cursor de teclado ponto a ponto (assets/crisis.js 182-188). */
  private onKeydown(e: KeyboardEvent): void {
    const delta = ARROW_DELTAS[e.key];
    if (delta !== undefined) {
      e.preventDefault();
      this.showAtIndex(this.kbIndex + delta);
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.showAtIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.showAtIndex(lastEvoIndex());
    }
  }

  /** Exibe e anuncia o ponto `idx` (assets/crisis.js 170-181). */
  private showAtIndex(idx: number): void {
    const n = getEvoSeries().length;
    if (!n) return;
    this.kbIndex = clampRange(idx, 0, n - 1);
    const rect = this.refs.plot.getBoundingClientRect();
    if (!rect.width) return;
    const relX = ((this.geometry.coords[this.kbIndex]?.[0] ?? 0) / this.geometry.w) * rect.width;
    this.refs.plot.classList.add('is-hover');
    this.paint(sampleEvoAt(relX, rect.width, this.geometry, getEvoSeries()), rect);
    this.announce(this.kbIndex);
  }

  /** Atualiza aria-valuenow/text e a regiao live (assets/crisis.js 177-180). */
  private announce(idx: number): void {
    /* v8 ignore next -- idx vem de kbIndex clampado em [0,n-1]; ?? 0 defensivo p/ noUncheckedIndexedAccess */
    const text = `${formatMentions(getEvoSeries()[idx] ?? 0)} menções, ${timeLabel(idx)}`;
    this.refs.plot.setAttribute('aria-valuenow', String(idx));
    this.refs.plot.setAttribute('aria-valuetext', text);
    if (this.refs.live) this.refs.live.textContent = text;
  }
}

/** Ultimo indice valido da serie atual; 0 quando vazia (paridade crisis.js 171). */
function lastEvoIndex(): number {
  return Math.max(0, getEvoSeries().length - 1);
}

/** Mapeia setas para o passo de indice (assets/crisis.js 183-184). */
const ARROW_DELTAS: Readonly<Record<string, number>> = {
  ArrowRight: 1,
  ArrowUp: 1,
  ArrowLeft: -1,
  ArrowDown: -1,
};

/** Clamp inclusivo de `v` em [lo, hi]. */
function clampRange(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
